import * as Sentry from '@sentry/angular-ivy';

export interface SentryConfig {
  dsn: string;
  environment: string;
  tracesSampleRate: number;
  tracePropagationTargets: string[];
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
  networkDetailAllowUrls: string[];
  ignoreErrors: string[];
  release?: string;
  maxBreadcrumbs?: number;
}

// Headers that must never leave the browser in a Sentry event (SF-R11 / #10165).
const SENSITIVE_HEADERS = ['authorization', 'cookie', 'set-cookie', 'x-xsrf-token', 'x-csrf-token'];

/** Keeps only the technical user id and strips cookies, credentials and payloads (SF-R11 / #10165). */
export function scrubSentryEvent<T extends Sentry.Event>(event: T): T {
  if (event.user) {
    event.user = event.user.id ? { id: event.user.id } : {};
  }
  if (event.request) {
    delete event.request.cookies;
    delete event.request.data;
    delete event.request.query_string;
    if (event.request.headers) {
      for (const key of Object.keys(event.request.headers)) {
        if (SENSITIVE_HEADERS.includes(key.toLowerCase())) {
          delete event.request.headers[key];
        }
      }
    }
  }
  return event;
}

export function initSentry(settings: SentryConfig): void {
  Sentry.init({
    dsn: settings.dsn,
    environment: settings.environment,

    // No IP address, cookies or user details by default (SF-R11 / #10165).
    sendDefaultPii: false,

    // Performance & Sampling
    tracesSampleRate: settings.tracesSampleRate,
    tracePropagationTargets: settings.tracePropagationTargets,

    // Session Replay settings
    replaysSessionSampleRate: settings.replaysSessionSampleRate,
    replaysOnErrorSampleRate: settings.replaysOnErrorSampleRate,

    // Error Filtering
    ignoreErrors: [
      ...settings.ignoreErrors,
      // Common errors to ignore
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      /^Network request failed$/,
      'ChunkLoadError',
    ],

    beforeSend(event) {
      // Don't send events in development
      if (settings.environment === 'development') {
        return null;
      }
      return scrubSentryEvent(event);
    },

    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.routingInstrumentation,
        tracePropagationTargets: [
          ...settings.tracePropagationTargets,
          /^\/api/, // Track all API calls
        ],
      }),

      // Session Replay with optimized settings
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        networkDetailAllowUrls: settings.networkDetailAllowUrls,
        networkRequestHeaders: ['content-type', 'accept', 'X-Custom-Header'],
        networkResponseHeaders: ['content-type', 'X-Custom-Header'],
        maskAllInputs: true,
      }),
    ],

    // Performance settings
    maxBreadcrumbs: 50, // Limit breadcrumbs for better performance
    attachStacktrace: true,
    autoSessionTracking: true,
    sendClientReports: true,

    // Release tracking
    release: settings.release, // Should be dynamic based on your build
  });
}
